/**
 * IMTaskModule — root component del modulo IMTask CRM integrato in ARCHI-HUB.
 *
 * Basato su ``App.js`` del bundle originale, ma senza AuthProvider (SSO tramite
 * ARCHI-HUB) e senza AuthPage (la route è già protetta da ProtectedRoute adminOnly).
 * Il wrapper root usa la classe ``imtask-root`` per scope-are gli stili CSS custom.
 */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import ReactConfetti from "react-confetti";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import PostItBoard from "./components/PostItBoard";
import RecurringTasksView from "./components/RecurringTasksView";
import CommissionCalculator from "./components/CommissionCalculator";
import PaymentScenarios from "./components/PaymentScenarios";
import TaskModal from "./components/TaskModal";
import RecurringModal from "./components/RecurringModal";
import ClientsView from "./components/ClientsView";
import { useAuth } from "./lib/auth";
import { QUOTES } from "./lib/constants";
import * as api from "./lib/api";
import { Loader2 } from "lucide-react";
import "./imtask.css";

export default function IMTaskModule() {
  // Assicura che window.__ARCHIHUB_USER__ / __CURRENT_USER_ID__ siano popolati
  // (l'auth shim li imposta in un useEffect al mount).
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-slate-50 text-slate-700">
        <Loader2 className="animate-spin mr-2" /> Caricamento IMTask CRM…
      </div>
    );
  }
  return (
    <div className="imtask-root pt-24 min-h-screen bg-cream">
      <Workspace />
    </div>
  );
}

function Workspace() {
  const [tasks, setTasks] = useState([]);
  const [postIts, setPostIts] = useState([]);
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [settings, setSettings] = useState({ logo: null, opCount: 0, totalSeconds: 0 });

  const [currentView, setCurrentView] = useState(() =>
    new URLSearchParams(window.location.search).get("view") === "calculator" ? "CALCULATOR" : "CRM"
  );
  const location = useLocation();
  useEffect(() => {
    const v = new URLSearchParams(location.search).get("view");
    setCurrentView(v === "calculator" ? "CALCULATOR" : "CRM");
  }, [location.search]);
  const [activeTab, setActiveTab] = useState("DASHBOARD");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [totalSeconds, setTotalSeconds] = useState(0);
  const [isIdle, setIsIdle] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const [celebration, setCelebration] = useState(null);
  const [autoSaveMsg, setAutoSaveMsg] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });

  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurringAlert, setRecurringAlert] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, p, r, s] = await Promise.all([
          api.listTasks(), api.listPostIts(), api.listRecurring(), api.getSettings(),
        ]);
        setTasks(t);
        setPostIts(p);
        setRecurringTasks(r);
        setSettings(s);
        setTotalSeconds(s.totalSeconds || 0);
      } catch (e) {
        console.error(e);
        toast.error("Errore nel caricamento dei dati IMTask");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Refresh automatico quando l'Assistente AI crea/modifica task IMTask
  useEffect(() => {
    const onAiToolDone = (e) => {
      const name = e?.detail?.name || "";
      if (name.includes("imtask")) {
        api.listTasks().then(setTasks).catch(() => {});
      }
    };
    window.addEventListener("ai-tool-done", onAiToolDone);
    return () => window.removeEventListener("ai-tool-done", onAiToolDone);
  }, []);

  useEffect(() => {
    const urgent = [];
    recurringTasks.forEach((t) => {
      if (!t.nextDueDate) return;
      const due = new Date(t.nextDueDate);
      const todayDate = new Date();
      due.setHours(0, 0, 0, 0);
      todayDate.setHours(0, 0, 0, 0);
      const diff = Math.ceil((due.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
      if (diff <= 7 && diff >= 0) urgent.push(t);
    });
    setRecurringAlert(urgent);
  }, [recurringTasks]);

  useEffect(() => {
    const update = () => { lastActivityRef.current = Date.now(); if (isIdle) setIsIdle(false); };
    window.addEventListener("mousemove", update);
    window.addEventListener("keydown", update);
    window.addEventListener("click", update);
    window.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("mousemove", update);
      window.removeEventListener("keydown", update);
      window.removeEventListener("click", update);
      window.removeEventListener("scroll", update);
    };
  }, [isIdle]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastActivityRef.current < 60000) {
        setTotalSeconds((prev) => prev + 1);
        setIsIdle(false);
      } else setIsIdle(true);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const persist = setInterval(() => {
      api.updateSettings({ totalSeconds }).catch(() => {});
    }, 30000);
    return () => clearInterval(persist);
  }, [totalSeconds]);

  useEffect(() => {
    const h = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    if (celebration) { const t = setTimeout(() => setCelebration(null), 2500); return () => clearTimeout(t); }
  }, [celebration]);
  useEffect(() => {
    if (autoSaveMsg) { const t = setTimeout(() => setAutoSaveMsg(false), 2500); return () => clearTimeout(t); }
  }, [autoSaveMsg]);
  useEffect(() => {
    if (confetti) { const t = setTimeout(() => setConfetti(false), 3000); return () => clearTimeout(t); }
  }, [confetti]);

  const exportBackupFile = useCallback(async () => {
    try {
      const data = await api.exportBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `IMTask_backup_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore export");
    }
  }, []);

  const bumpOpCount = useCallback(async () => {
    const next = (settings.opCount || 0) + 1;
    if (next >= 10) {
      await exportBackupFile();
      setAutoSaveMsg(true);
      const s = await api.updateSettings({ opCount: 0 });
      setSettings((prev) => ({ ...prev, opCount: s.opCount }));
    } else {
      const s = await api.updateSettings({ opCount: next });
      setSettings((prev) => ({ ...prev, opCount: s.opCount }));
    }
  }, [settings.opCount, exportBackupFile]);

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check dimensione file: FastAPI accetta fino a ~10 MB, ma segnaliamo prima
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`File troppo grande (${Math.round(file.size / 1024 / 1024)} MB). Max 10 MB.`);
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      // Fase 1: parsing JSON
      let parsed;
      try {
        parsed = JSON.parse(e.target?.result);
      } catch (err) {
        toast.error(`File JSON malformato: ${err.message?.slice(0, 100) || "impossibile leggere il file"}`);
        event.target.value = "";
        return;
      }

      // Fase 2: validazione struttura (deve avere almeno UN array di dati)
      let payload = parsed;
      if (Array.isArray(parsed)) payload = { tasks: parsed };
      const hasTasks = Array.isArray(payload.tasks);
      const hasPostIts = Array.isArray(payload.postIts) || Array.isArray(payload.postits);
      const hasRecurring = Array.isArray(payload.recurringTasks) || Array.isArray(payload.recurring);
      const hasSuppliers = Array.isArray(payload.suppliers);
      if (!hasTasks && !hasPostIts && !hasRecurring && !hasSuppliers) {
        toast.error("Formato non riconosciuto: manca almeno un array 'tasks', 'postIts' o 'recurringTasks'");
        event.target.value = "";
        return;
      }

      const total = (payload.tasks?.length || 0) + (payload.postIts?.length || payload.postits?.length || 0)
        + (payload.recurringTasks?.length || payload.recurring?.length || 0)
        + (payload.suppliers?.length || 0);

      // Fase 3: conferma sovrascrittura (l'import sostituisce completamente i dati esistenti)
      if (!window.confirm(
        `Importare ${total} elementi da questo backup?\n\n⚠️ ATTENZIONE: l'import SOVRASCRIVE completamente i tuoi dati IMTask attuali (task, post-it, ricorrenti, fornitori CRM).`
      )) {
        event.target.value = "";
        return;
      }

      // Fase 4: upload al backend
      try {
        const res = await api.importBackup(payload);
        const [t, p, r] = await Promise.all([api.listTasks(), api.listPostIts(), api.listRecurring()]);
        setTasks(t); setPostIts(p); setRecurringTasks(r);
        const imp = res?.imported || {};
        toast.success(
          `Import riuscito: ${imp.tasks || 0} task · ${imp.postits || 0} post-it · ${imp.recurring || 0} ricorrenti${imp.suppliers ? ` · ${imp.suppliers} fornitori` : ""}`,
          { duration: 6000 },
        );
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        let msg = "Errore durante l'import";
        if (status === 401 || status === 403) msg = "Sessione scaduta o non autorizzato. Effettua il login di nuovo.";
        else if (status === 413) msg = "File troppo grande per il server.";
        else if (typeof detail === "string") msg = detail;
        else if (detail) msg = JSON.stringify(detail).slice(0, 200);
        else if (err?.message) msg = err.message;
        toast.error(msg, { duration: 8000 });
        // Log completo per il debug
        console.error("[IMTask import] errore", { status, detail, err });
      }
      event.target.value = "";
    };
    reader.onerror = () => {
      toast.error("Impossibile leggere il file dal disco");
      event.target.value = "";
    };
    reader.readAsText(file);
  };

  const openModal = (task) => {
    setEditingTask(task || null);
    setIsModalOpen(true);
  };

  const saveTask = async (partial) => {
    try {
      if (editingTask) {
        const upd = await api.updateTask(editingTask.id, { ...editingTask, ...partial });
        setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
      } else {
        const created = await api.createTask(partial);
        setTasks((prev) => [...prev, created]);
      }
      setIsModalOpen(false);
      setActiveTab("DASHBOARD");
      toast.success(editingTask ? "Attività aggiornata" : "Attività creata");
      bumpOpCount();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const removeTask = async (id) => {
    if (!window.confirm("Eliminare attività?")) return;
    try {
      await api.deleteTask(id);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      toast.success("Attività eliminata");
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const toggleComplete = async (id) => {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;
    const newCompleted = !task.completed;
    if (newCompleted) {
      const messages = ["Ottimo lavoro!", "Continua così!", "Attività completata!"];
      setCelebration(messages[Math.floor(Math.random() * messages.length)]);
      setConfetti(true);
    }
    try {
      const upd = await api.updateTask(id, {
        ...task, completed: newCompleted,
        completedDate: newCompleted ? new Date().toISOString() : null,
      });
      setTasks((prev) => prev.map((t) => (t.id === id ? upd : t)));
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const addPostIt = async () => {
    try {
      const created = await api.createPostIt({ text: "" });
      setPostIts((prev) => [created, ...prev]);
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const updatePostItText = (id, text) => {
    setPostIts((prev) => prev.map((p) => (p.id === id ? { ...p, text } : p)));
  };

  const persistPostIt = async (id, text) => {
    try { await api.updatePostIt(id, { text }); } catch (e) { /* ignore */ }
  };

  const removePostIt = async (id) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    try {
      await api.deletePostIt(id);
      setPostIts((prev) => prev.filter((p) => p.id !== id));
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const openRecurringModal = (task) => {
    setEditingRecurring(task || null);
    setIsRecurringModalOpen(true);
  };

  const saveRecurring = async (formData) => {
    try {
      if (editingRecurring) {
        const upd = await api.updateRecurring(editingRecurring.id, formData);
        setRecurringTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
      } else {
        const created = await api.createRecurring(formData);
        setRecurringTasks((prev) => [...prev, created]);
      }
      setIsRecurringModalOpen(false);
      toast.success("Scadenza salvata");
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const completeRecurring = async (task) => {
    const currentDue = new Date(task.nextDueDate);
    let nextDate = new Date(currentDue);
    if (task.frequency === "1M") nextDate.setMonth(nextDate.getMonth() + 1);
    else if (task.frequency === "3M") nextDate.setMonth(nextDate.getMonth() + 3);
    else if (task.frequency === "6M") nextDate.setMonth(nextDate.getMonth() + 6);
    else if (task.frequency === "1Y") nextDate.setFullYear(nextDate.getFullYear() + 1);
    else if (task.frequency === "MANUAL") {
      const inputDate = window.prompt("Prossima scadenza (AAAA-MM-GG):", new Date().toISOString().split("T")[0]);
      if (!inputDate) return;
      nextDate = new Date(inputDate);
      if (Number.isNaN(nextDate.getTime())) { toast.error("Data non valida"); return; }
    }
    const y = nextDate.getFullYear();
    const m = String(nextDate.getMonth() + 1).padStart(2, "0");
    const d = String(nextDate.getDate()).padStart(2, "0");
    const nextStr = `${y}-${m}-${d}`;
    try {
      const upd = await api.updateRecurring(task.id, { ...task, nextDueDate: nextStr });
      setRecurringTasks((prev) => prev.map((t) => (t.id === task.id ? upd : t)));
      setCelebration("Scadenza aggiornata!");
      setIsRecurringModalOpen(false);
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const removeRecurring = async (id) => {
    if (!window.confirm("Eliminare scadenza ciclica?")) return;
    try {
      await api.deleteRecurring(id);
      setRecurringTasks((prev) => prev.filter((t) => t.id !== id));
      bumpOpCount();
    } catch (e) { toast.error("Errore"); }
  };

  const setLogo = async (logo) => {
    setSettings((prev) => ({ ...prev, logo }));
    try { await api.updateSettings({ logo }); } catch (e) { /* ignore */ }
  };

  const dailyQuote = useMemo(() => QUOTES[new Date().getDate() % QUOTES.length], []);

  return (
    <div
      className="flex h-[calc(100vh-96px-3rem)] bg-slate-50 text-slate-800 overflow-hidden font-ibm rounded-lg mx-4 mb-6 border border-ink/10 shadow-sm"
      data-testid="imtask-workspace"
    >
      <Sidebar
        currentView={currentView}
        setCurrentView={setCurrentView}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tasks={tasks}
        postIts={postIts}
        recurringTasks={recurringTasks}
        recurringAlerts={recurringAlert}
        logo={settings.logo}
        setLogo={setLogo}
        importData={handleImport}
        exportData={exportBackupFile}
      />

      <main className="flex-1 flex flex-col overflow-hidden relative">
        {autoSaveMsg && (
          <div className="absolute top-4 left-1/2 z-50 autosave-anim -translate-x-1/2">
            <div className="bg-ink text-white px-6 py-3 rounded-full shadow-lg flex items-center space-x-3">
              <span className="font-semibold text-sm">Backup Automatico Scaricato</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-500">Caricamento...</div>
        ) : currentView === "CRM" ? (
          <Dashboard
            tasks={tasks} postIts={postIts}
            activeTab={activeTab} setActiveTab={setActiveTab}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            openModal={openModal} deleteTask={removeTask}
            toggleComplete={toggleComplete} setView={setCurrentView}
            isIdle={isIdle} totalSeconds={totalSeconds} dailyQuote={dailyQuote}
          />
        ) : currentView === "CLIENTS" ? (
          <ClientsView openTaskModal={openModal} />
        ) : currentView === "CALCULATOR" ? (
          <CommissionCalculator />
        ) : currentView === "SCENARIOS" ? (
          <PaymentScenarios />
        ) : currentView === "POSTITS" ? (
          <PostItBoard
            postIts={postIts} addPostIt={addPostIt}
            updatePostIt={updatePostItText} persistPostIt={persistPostIt} deletePostIt={removePostIt}
          />
        ) : (
          <RecurringTasksView
            tasks={recurringTasks} openModal={openRecurringModal}
            completeTask={completeRecurring} deleteTask={removeRecurring}
          />
        )}
      </main>

      {isModalOpen && (
        <TaskModal task={editingTask} activeCategory={activeTab}
          onClose={() => setIsModalOpen(false)} onSave={saveTask} />
      )}

      {isRecurringModalOpen && (
        <RecurringModal task={editingRecurring}
          onClose={() => setIsRecurringModalOpen(false)}
          onSave={saveRecurring} onComplete={completeRecurring} />
      )}

      {celebration && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none">
          <div className="bg-white/95 backdrop-blur-md px-10 py-8 rounded-3xl shadow-2xl border-4 border-gold celebration-anim flex flex-col items-center">
            <div className="text-6xl mb-2">🎉</div>
            <h2 className="text-3xl font-black font-outfit text-transparent bg-clip-text bg-gradient-to-r from-gold to-ink uppercase tracking-wider">
              {celebration}
            </h2>
          </div>
        </div>
      )}

      {confetti && (
        <ReactConfetti width={dims.w} height={dims.h} numberOfPieces={220} recycle={false} gravity={0.25} />
      )}
    </div>
  );
}
